import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CollapsibleSection from '../../CollapsibleSection';
import { useToast } from '../../Toast';
import { ComplianceItem, loadComplianceItems, requestFormRenewal } from '../../../services/ComplianceService';
import { ComplianceStatus, summarize } from '../../../utils/compliance';

interface LegalsCardProps {
  practiceOwnerId?: string;
}

const STATUS_LABEL: Record<ComplianceStatus, string> = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  incomplete: 'Missing',
};

const STATUS_COLOR: Record<ComplianceStatus, string> = {
  overdue: '#ef4444',
  due_soon: '#D4A017',
  incomplete: '#60a5fa',
};

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

/**
 * "Legals": every patient form and staff document that is missing, overdue or
 * coming due for renewal — the list to have ready if the practice is audited.
 */
const LegalsCard: React.FC<LegalsCardProps> = ({ practiceOwnerId }) => {
  const toast = useToast();
  const [items, setItems] = useState<ComplianceItem[] | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<ComplianceStatus | 'all'>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setItems(await loadComplianceItems(practiceOwnerId || ''));
    } catch (err) {
      console.error('Legals load failed:', err);
      setError('Could not load the compliance list.');
      setItems([]);
    }
  }, [practiceOwnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => summarize((items || []).map((i) => ({ status: i.status }))), [items]);
  const visible = (items || []).filter((i) => filter === 'all' || i.status === filter);

  const handleRenew = async (item: ComplianceItem) => {
    if (!window.confirm(`Ask ${item.subjectName} to complete "${item.label}" again? It will reappear in their portal.`)) return;
    setBusyId(item.id);
    try {
      await requestFormRenewal(item.id);
      toast.success('Renewal requested', `${item.subjectName} will see "${item.label}" in their portal.`);
      await load();
    } catch (err: any) {
      toast.error('Could not request renewal', err.message || 'Something went wrong.');
    }
    setBusyId(null);
  };

  const exportCsv = () => {
    const rows = [
      ['Status', 'Type', 'Name', 'Document', 'Due date'],
      ...visible.map((i) => [
        STATUS_LABEL[i.status],
        i.kind === 'patient_form' ? 'Patient form' : 'Staff document',
        i.subjectName,
        i.label,
        formatDate(i.dueDate),
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legals-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const needsAttention = counts.overdue + counts.due_soon + counts.incomplete;
  const title = items === null ? 'Legals' : `Legals (${needsAttention})`;
  const variant = counts.overdue > 0 ? 'danger' : needsAttention > 0 ? 'warning' : 'success';

  return (
    <CollapsibleSection title={title} variant={variant} defaultExpanded={counts.overdue > 0}>
      {items === null && <p style={{ color: '#d1d5db' }}>Checking forms and documents…</p>}
      {error && <p style={{ color: '#ef4444' }}>{error}</p>}

      {items !== null && !error && needsAttention === 0 && (
        <p style={{ color: '#d1d5db', margin: 0 }}>
          Everything is current. Patient forms and staff documents are all up to date.
        </p>
      )}

      {items !== null && needsAttention > 0 && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
            {(['all', 'overdue', 'due_soon', 'incomplete'] as const).map((key) => {
              const count = key === 'all' ? needsAttention : counts[key];
              const active = filter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  aria-pressed={active}
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '999px',
                    border: `1px solid ${key === 'all' ? '#6b7280' : STATUS_COLOR[key]}`,
                    background: active ? (key === 'all' ? '#374151' : STATUS_COLOR[key]) : 'transparent',
                    color: active ? '#fff' : '#d1d5db',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  {key === 'all' ? 'All' : STATUS_LABEL[key]} ({count})
                </button>
              );
            })}
            <button
              type="button"
              onClick={exportCsv}
              style={{ marginLeft: 'auto', padding: '0.35rem 0.75rem', borderRadius: '6px', border: '1px solid #6b7280', background: 'transparent', color: '#d1d5db', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              Export for audit (CSV)
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', color: '#e5e7eb' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#9ca3af' }}>
                  <th style={{ padding: '0.4rem' }}>Status</th>
                  <th style={{ padding: '0.4rem' }}>Name</th>
                  <th style={{ padding: '0.4rem' }}>Document</th>
                  <th style={{ padding: '0.4rem' }}>Due</th>
                  <th style={{ padding: '0.4rem' }} />
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr key={`${item.kind}-${item.id}`} style={{ borderTop: '1px solid #374151' }}>
                    <td style={{ padding: '0.4rem', color: STATUS_COLOR[item.status], fontWeight: 600 }}>
                      {STATUS_LABEL[item.status]}
                    </td>
                    <td style={{ padding: '0.4rem' }}>
                      {item.subjectName}
                      <span style={{ color: '#9ca3af' }}> · {item.kind === 'patient_form' ? 'patient' : 'staff'}</span>
                    </td>
                    <td style={{ padding: '0.4rem' }}>{item.label}</td>
                    <td style={{ padding: '0.4rem' }}>{formatDate(item.dueDate)}</td>
                    <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                      {item.kind === 'patient_form' && item.status !== 'incomplete' && (
                        <button
                          type="button"
                          disabled={busyId === item.id}
                          onClick={() => handleRenew(item)}
                          style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #D4A017', background: 'transparent', color: '#D4A017', cursor: 'pointer', fontSize: '0.8rem' }}
                        >
                          {busyId === item.id ? 'Sending…' : 'Request renewal'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

export default LegalsCard;
